import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { ComponentToPrint } from "./ComponentToPrint";

import ApiHelper from "../../../Helpers/ApiHelper";
import Overlay from "../../SharedComponents/Overlay";
import WholePdfFile from "./PdfFile/WholePdfFile";
import DownloadButtonUi from "./DownloadButtonUi";
import AskAiButtonUi from "./AskAiButtonUi";
import AiResponseModal from "./AiResponseModal";
import { generatePdfBlob } from "../Helpers/generatePdfBlob";
import "../Styles/PdfFile.css";
import { UpdateAlertModalData } from "../../SharedComponents/GeneralModals/Store";

export default function InventoryPdfButton({ inventoryResultId, resultData, uiType, docTitle }) {
  const user = useSelector((state) => state.auth.user);

  const [showOverlay, setShowOverlay] = useState(false);
  const [pdfComponent, setPdfComponent] = useState(null);
  const componentRef = useRef(null);

  // AI Analysis state
  const [showAiModal, setShowAiModal] = useState(false);
  const [autoUploadFile, setAutoUploadFile] = useState(null);
  const [isGeneratingForAi, setIsGeneratingForAi] = useState(false);
  const aiPdfRef = useRef(null);

  const dispatch = useDispatch();

  let StartPdfExportProcess = async () => {
    setShowOverlay(true);
    if (resultData) MapResponse(resultData);
    else if (inventoryResultId) {
      ApiHelper.getAsync(
        "ticket/GetInventoryCalculation",
        { id: inventoryResultId },
        function (response) {
          if (response.data.statusCode) MapResponse(response.data.data);
          else
            dispatch(
              UpdateAlertModalData({
                isShown: false,
                header: "calculationError",
                text: response.data.message,
              })
            );
        },
        user.token
      );
    } else {
      setShowOverlay(false);
      alert("problem");
    }
  };

  let MapResponse = (result) => {
    let demography = {
      name: result.demography.name,
      email: result.demography.email,
      department: result.demography.department,
      faculty: result.demography.faculty,
      school: result.demography.school,
      birthYear: result.demography.birthYear,
      gender: result.demography.gender,
      experience: result.demography.experience,
    };

    let appData = {
      pdfCoverImageName: result.pdfCoverImageName,
      pdfInfoImageName: result.pdfInfoImageName,
      name: result.applicationName,
    };

    const scheme = JSON.parse(result.pdfJson);
    const calculation = JSON.parse(result.calculationJson);

    //TODO: check here for backend problems

    scheme.forEach((page) => HandleEachPage(page, calculation));

    setPdfComponent(
      <WholePdfFile
        StartDownloadPdf={HandlePrint}
        scheme={scheme}
        appData={appData}
        demography={demography}
        type={"inventory"}
        qrURL={CreateQrUrl()}
      />
    );
  };

  let HandleEachPage = (pageReportItems, calculation) => {
    let calculatedScopes = calculation?.calculatedScopes;
    let consistencyScore = calculation.consistencyScore?.score;
    let inventoryLeanNumbers = calculation?.inventoryLeanNumbers;
    pageReportItems.forEach((reportItem) => {
      if (reportItem.htmlEl) return;
      else if (reportItem.componentName === "AnsweringTendencyEntity") {
        reportItem.consistencyScore = consistencyScore;
        reportItem.inventoryLeanNumbers = inventoryLeanNumbers;
      } else if (reportItem.subScopeId)
        reportItem.score = calculatedScopes.find(
          (x) => x.isSub && x.scopeId === reportItem.subScopeId
        )?.calculatedScore;
      else {
        reportItem.score = calculatedScopes.find(
          (x) => x.isSub === false && x.scopeId === reportItem.scopeId
        )?.calculatedScore;
      }
    });
  };

  const CreateQrUrl = () => {
    return window.location.origin + "/#/iResult/" + inventoryResultId;
  };

  const HandlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: docTitle,
    onBeforePrint: () => {
      setShowOverlay(false);
    },
    onAfterPrint: () => {
      setPdfComponent(null);
    },
  });

  // Start AI analysis - generate PDF and send to AI
  const handleAskAi = async () => {
    setIsGeneratingForAi(true);
    setShowOverlay(true);
    setAutoUploadFile(null);

    // Fetch data first if not already available
    if (resultData) {
      await generatePdfForAi(resultData);
    } else if (inventoryResultId) {
      ApiHelper.getAsync(
        "ticket/GetInventoryCalculation",
        { id: inventoryResultId },
        async function (response) {
          if (response.data.statusCode) {
            await generatePdfForAi(response.data.data);
          } else {
            setShowOverlay(false);
            setIsGeneratingForAi(false);
            dispatch(
              UpdateAlertModalData({
                isShown: false,
                header: "calculationError",
                text: response.data.message,
              })
            );
          }
        },
        user.token
      );
    } else {
      setShowOverlay(false);
      setIsGeneratingForAi(false);
      alert("problem");
    }
  };

  const generatePdfForAi = async (result) => {
    let demography = {
      name: result.demography.name,
      email: result.demography.email,
      department: result.demography.department,
      faculty: result.demography.faculty,
      school: result.demography.school,
      birthYear: result.demography.birthYear,
      gender: result.demography.gender,
      experience: result.demography.experience,
    };

    let appData = {
      pdfCoverImageName: result.pdfCoverImageName,
      pdfInfoImageName: result.pdfInfoImageName,
      name: result.applicationName,
    };

    const scheme = JSON.parse(result.pdfJson);
    const calculation = JSON.parse(result.calculationJson);

    scheme.forEach((page) => HandleEachPage(page, calculation));

    // Set component for rendering
    setPdfComponent(
      <WholePdfFile
        StartDownloadPdf={() => {}}
        scheme={scheme}
        appData={appData}
        demography={demography}
        type={"inventory"}
        qrURL={CreateQrUrl()}
      />
    );

    // Wait for component to render
    setTimeout(async () => {
      try {
        console.log("AI PDF generation - aiPdfRef.current:", aiPdfRef.current);
        if (aiPdfRef.current) {
          console.log("Generating PDF blob...");
          const pdfFile = await generatePdfBlob(
            aiPdfRef.current,
            `${docTitle || "inventory-report"}.pdf`
          );
          console.log("PDF file generated:", pdfFile.name, pdfFile.size, "bytes");
          setAutoUploadFile(pdfFile);
          setShowAiModal(true);
        } else {
          console.error("aiPdfRef.current is null - component not rendered yet");
        }
      } catch (error) {
        console.error("Error generating PDF for AI:", error);
      } finally {
        setShowOverlay(false);
        setIsGeneratingForAi(false);
        setPdfComponent(null);
      }
    }, 1500);
  };

  return (
    <>
      {showOverlay && <Overlay text={isGeneratingForAi ? "AI için PDF Hazırlanıyor" : "Pdf Dosyası Hazırlanıyor"} />}
      <div className="d-flex align-items-center">
        <DownloadButtonUi StartPdfExportProcess={StartPdfExportProcess} uiType={uiType} />
        <AskAiButtonUi onAskAi={handleAskAi} uiType={uiType} />
      </div>
      <AiResponseModal
        show={showAiModal}
        onHide={() => {
          setShowAiModal(false);
          setAutoUploadFile(null);
        }}
        autoUploadFile={autoUploadFile}
        analysisType="inventory"
      />
      <ComponentToPrint ref={componentRef}>
        <div className="onlyPrint" ref={componentRef}>
          {pdfComponent}
        </div>
      </ComponentToPrint>
      {/* Hidden container for AI PDF generation - must be visible for html2canvas */}
      {isGeneratingForAi && pdfComponent && (
        <div
          ref={aiPdfRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: 0,
            width: "210mm",
            background: "white",
          }}
        >
          {pdfComponent}
        </div>
      )}
    </>
  );
}
